import Bugsnag from '@bugsnag/js'
import { defineStore } from 'pinia'

import { checkout } from '@/services/cart'
import { FetchError } from '@/services/lib/fetch'
import { useProductStore } from '@/stores/product'

import {
  freeThresholdGrams,
  freeThresholdPence,
  lowShippingPrice,
  lowThresholdGrams,
  shippingPrice,
} from '@/constants/shipping'
import { extractShopifyId } from '@/lib/gid'
import { AsyncOperationState } from './lib/AsyncOperationState'

export interface CartItem {
  productId: string
  variantId: string
  quantity: number
  price: string
  weight: number | null
  discount: number
}

export type CheckoutErrorInfo = {
  name: string
  message: string
  httpStatus: number | null
  apiCode: string | null
  apiError: string | null
}

const checkoutItemMetadata = (items: CartItem[]) =>
  items.map(({ productId, variantId, quantity, price, discount }) => ({
    productId,
    variantId,
    quantity,
    price,
    discount,
  }))

const stringifyApiError = (errors: unknown): string | null => {
  if (errors == null) return null
  if (typeof errors === 'string') return errors.slice(0, 500)
  try {
    return JSON.stringify(errors).slice(0, 500)
  } catch {
    return String(errors).slice(0, 500)
  }
}

export const getCheckoutErrorInfo = (e: unknown): CheckoutErrorInfo => {
  if (e instanceof FetchError) {
    const body = e.errors
    const apiCode =
      body &&
      typeof body === 'object' &&
      'code' in body &&
      typeof body.code === 'string'
        ? body.code
        : null
    return {
      name: e.name,
      message: e.message,
      httpStatus: e.res.status,
      apiCode,
      apiError: stringifyApiError(e.errors),
    }
  }
  if (e instanceof Error) {
    return {
      name: e.name,
      message: e.message,
      httpStatus: null,
      apiCode: null,
      apiError: null,
    }
  }
  return {
    name: 'unknown',
    message: String(e),
    httpStatus: null,
    apiCode: null,
    apiError: null,
  }
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    items: [] as CartItem[],
    completedCheckout: null as {
      items: CartItem[]
      total: number
      orderId: string
    } | null,
    checkoutState: AsyncOperationState.IDLE,
    lastCheckoutError: null as CheckoutErrorInfo | null,
  }),
  actions: {
    add: function (
      productId: string,
      variantId: string,
      quantity: number,
      price: string,
      weight: number,
      discount: number,
    ) {
      const existingItem = this.items.find(
        (item) => item.variantId === extractShopifyId(variantId),
      )
      if (existingItem) {
        existingItem.productId = extractShopifyId(productId)
        existingItem.variantId = extractShopifyId(variantId)
        existingItem.quantity += quantity
        existingItem.price = price
        existingItem.weight = weight
        existingItem.discount = discount
      } else {
        this.items.push({
          productId: extractShopifyId(productId),
          variantId: extractShopifyId(variantId),
          quantity,
          price,
          weight,
          discount,
        })
      }
      this.loadProducts()
    },
    remove: function (variantId: string) {
      this.items = this.items.filter((i) => i.variantId !== variantId)
    },
    updateItem: function (variantId: string, updates: Partial<CartItem>) {
      const index = this.items.findIndex((i) => i.variantId === variantId)
      if (index !== -1) {
        this.items[index] = { ...this.items[index], ...updates }
      }
    },
    loadProducts: async function () {
      const productStore = useProductStore()
      const results = await Promise.allSettled(
        this.items.map((item) => {
          return productStore.loadProduct(item.productId)
        }),
      )
      const toRemove = this.items
        .map((item, index) => {
          if (
            results[index].status === 'fulfilled' &&
            results[index].value?.product
          ) {
            return null
          } else {
            return item
          }
        })
        .filter(Boolean) as CartItem[]

      toRemove.forEach((item) => {
        this.remove(item.variantId)
      })
    },
    checkout: async function (rechargeId: string, addressId: string) {
      this.checkoutState = AsyncOperationState.IN_PROGRESS
      this.lastCheckoutError = null
      const items = checkoutItemMetadata(this.items)
      try {
        const charge = await checkout(rechargeId, addressId, this.items)
        const orderId = charge.external_order_id?.ecommerce
        if (!orderId) {
          Bugsnag.notify(
            new Error(
              'Checkout succeeded but response missing external_order_id',
            ),
            (event) => {
              event.context = 'cart/checkout'
              event.addMetadata('checkout', {
                rechargeId,
                addressId,
                itemCount: items.length,
                items,
                chargeId: charge.id ?? null,
              })
            },
          )
        }
        this.checkoutState = AsyncOperationState.DONE
        this.completedCheckout = {
          items: this.items,
          total: this.subtotal + this.shipping,
          orderId: orderId ?? '',
        }
        this.items = []
        return
      } catch (e) {
        this.lastCheckoutError = getCheckoutErrorInfo(e)
        Bugsnag.notify(e as Error, (event) => {
          event.context = 'cart/checkout'
          event.addMetadata('checkout', {
            rechargeId,
            addressId,
            itemCount: items.length,
            items,
            ...this.lastCheckoutError,
          })
        })
        this.checkoutState = AsyncOperationState.ERRORED
      }
    },
    resetCheckoutState() {
      this.checkoutState = AsyncOperationState.IDLE
      this.lastCheckoutError = null
    },
  },
  getters: {
    subtotal(state) {
      return state.items.reduce((acc, item) => {
        const price = parseFloat(item.price) * 100
        return acc + item.quantity * (price - price * (item.discount / 100))
      }, 0)
    },
    shipping(): number {
      return this.amountRemainingForFreeShipping <= 0
        ? 0
        : this.weight >= lowThresholdGrams
          ? lowShippingPrice
          : shippingPrice
    },
    weight(state) {
      return state.items.reduce((acc, item) => {
        return acc + item.quantity * (item.weight ?? 0)
      }, 0)
    },
    percentOffFreeShipping(): number {
      return this.weight >= freeThresholdGrams
        ? 0
        : 100 - Math.min(1, this.subtotal / freeThresholdPence) * 100
    },
    amountRemainingForFreeShipping(): number {
      return this.weight >= freeThresholdGrams
        ? 0
        : freeThresholdPence - this.subtotal
    },
  },
})
